import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';

// Pages
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { LearningPathPage } from './pages/LearningPathPage';
import { LessonPage } from './pages/LessonPage';
import { MarketInsightPage } from './pages/MarketInsightPage';
import { ChecklistPage } from './pages/ChecklistPage';
import { ResourceVaultPage } from './pages/ResourceVaultPage';
import { LiveSessionPage } from './pages/LiveSessionPage';
import { CertificatePage } from './pages/CertificatePage';
import { SearchPage } from './pages/SearchPage';
import { AdminLoginPage } from './pages/AdminLoginPage';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminLevelsPage } from './pages/AdminLevelsPage';
import { AdminInsightsPage } from './pages/AdminInsightsPage';
import { AdminResourcesPage } from './pages/AdminResourcesPage';
import { AdminSessionsPage } from './pages/AdminSessionsPage';
import { AdminInvitationsPage } from './pages/AdminInvitationsPage';
import { AdminUsersPage } from './pages/AdminUsersPage';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading, isAdmin } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (isAdmin) {
        return <Navigate to="/admin" replace />;
    }
    
    return children;
};

// Admin Protected Route Component
const AdminRoute = ({ children }) => {
    const { isAuthenticated, loading, isAdmin } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-900">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }
    
    if (!isAuthenticated || !isAdmin) {
        return <Navigate to="/admin/login" replace />;
    }
    
    return children;
};

// Public Route Component (redirect if already logged in)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading, isAdmin } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }
    
    if (isAuthenticated) {
        if (isAdmin) {
            return <Navigate to="/admin" replace />;
        }
        return <Navigate to="/dashboard" replace />;
    }
    
    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/admin/login" element={<PublicRoute><AdminLoginPage /></PublicRoute>} />

            {/* Protected User Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/learning" element={<ProtectedRoute><LearningPathPage /></ProtectedRoute>} />
            <Route path="/learning/:level" element={<ProtectedRoute><LearningPathPage /></ProtectedRoute>} />
            <Route path="/lesson/:id" element={<ProtectedRoute><LessonPage /></ProtectedRoute>} />
            <Route path="/insights" element={<ProtectedRoute><MarketInsightPage /></ProtectedRoute>} />
            <Route path="/checklist" element={<ProtectedRoute><ChecklistPage /></ProtectedRoute>} />
            <Route path="/resources" element={<ProtectedRoute><ResourceVaultPage /></ProtectedRoute>} />
            <Route path="/sessions" element={<ProtectedRoute><LiveSessionPage /></ProtectedRoute>} />
            <Route path="/certificates" element={<ProtectedRoute><CertificatePage /></ProtectedRoute>} />
            <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/levels" element={<AdminRoute><AdminLevelsPage /></AdminRoute>} />
            <Route path="/admin/insights" element={<AdminRoute><AdminInsightsPage /></AdminRoute>} />
            <Route path="/admin/checklists" element={<AdminRoute><AdminLevelsPage /></AdminRoute>} />
            <Route path="/admin/resources" element={<AdminRoute><AdminResourcesPage /></AdminRoute>} />
            <Route path="/admin/sessions" element={<AdminRoute><AdminSessionsPage /></AdminRoute>} />
            <Route path="/admin/invitations" element={<AdminRoute><AdminInvitationsPage /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <LanguageProvider>
            <AuthProvider>
                <BrowserRouter>
                    <AppRoutes />
                    <Toaster 
                        position="top-right"
                        richColors
                        closeButton
                    />
                </BrowserRouter>
            </AuthProvider>
        </LanguageProvider>
    );
}

export default App;
